import { useEffect, useMemo, useState } from "react";

const LANG_OPTIONS = [
  { code: "pt", label: "Portuguese" },
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
];

const STORAGE_KEY = "site_language";

function readStoredLanguage() {
  if (typeof window === "undefined") return "pt";
  const fromStorage = window.localStorage.getItem(STORAGE_KEY);
  return LANG_OPTIONS.some((item) => item.code === fromStorage) ? fromStorage : "pt";
}

function dispatchChange(select) {
  select.dispatchEvent(new Event("change", { bubbles: true }));
}

function applyLanguageToTranslator(code) {
  const select = document.querySelector(".goog-te-combo");
  if (!select) return false;
  select.value = code;
  dispatchChange(select);
  return true;
}

function createTranslateElement() {
  if (!window.google?.translate?.TranslateElement) return;
  if (document.querySelector(".goog-te-combo")) return;

  const container = document.getElementById("google_translate_element");
  if (!container) return;

  container.innerHTML = "";
  new window.google.translate.TranslateElement(
    {
      pageLanguage: "pt",
      includedLanguages: "pt,en,es,fr",
      autoDisplay: false,
    },
    "google_translate_element"
  );
}

function ensureTranslateScript() {
  return new Promise((resolve) => {
    if (window.google?.translate?.TranslateElement) {
      createTranslateElement();
      resolve();
      return;
    }

    window.googleTranslateElementInit = () => {
      createTranslateElement();
      resolve();
    };

    const existingScript = document.querySelector('script[data-google-translate="true"]');
    if (existingScript) return;

    const script = document.createElement("script");
    script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    script.defer = true;
    script.setAttribute("data-google-translate", "true");
    document.body.appendChild(script);
  });
}

function retryApplyLanguage(code, attempt = 0) {
  const applied = applyLanguageToTranslator(code);
  if (applied || attempt > 20) return;
  window.setTimeout(() => retryApplyLanguage(code, attempt + 1), 200);
}

export default function LanguageSwitcher() {
  const [language, setLanguage] = useState("pt");
  const activeLanguage = useMemo(
    () => LANG_OPTIONS.find((item) => item.code === language)?.label || "Portuguese",
    [language]
  );

  useEffect(() => {
    const saved = readStoredLanguage();
    setLanguage(saved);

    ensureTranslateScript().then(() => {
      retryApplyLanguage(saved);
    });
  }, []);

  const handleChange = (event) => {
    const nextLanguage = event.target.value;
    setLanguage(nextLanguage);
    window.localStorage.setItem(STORAGE_KEY, nextLanguage);
    retryApplyLanguage(nextLanguage);
  };

  return (
    <>
      <div id="google_translate_element" className="hidden" />
      <div className="fixed bottom-4 right-4 z-[1000]" data-testid="language-switcher">
        <label
          htmlFor="language-switcher-select"
          className="sr-only"
        >
          Site language
        </label>
        <select
          id="language-switcher-select"
          value={language}
          onChange={handleChange}
          className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-800 shadow-lg shadow-slate-900/10 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
          aria-label={`Site language: ${activeLanguage}`}
        >
          {LANG_OPTIONS.map((option) => (
            <option key={option.code} value={option.code}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}

